import React from 'react';
import isEmpty from 'lodash/isEmpty';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import { css } from '@lyearn/style';
import { DateVariant, Stack, Typography } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import { ErrorPosition, Fields, FieldType, useFormFieldValue } from '@/components/Form';
import useUser from '@/components/Form/components/Conditions/hooks/useUser';
import VisibilitySettingsFormField from '@/components/Form/components/VisibilitySettings';
import { VisibilitySettingsFormFieldProps } from '@/components/Form/components/VisibilitySettings/types';
import { getRandomIconSettings } from '@/components/IconSettingsColorPicker/utils';
import { MultiStepSection } from '@/components/MultiStepForm/types';
import UserSearchInput from '@/components/UsersSearch';
import AddCover from '@/modules/events/views/EventEditor/components/AddCover';
import ClientSelect from '@/modules/tasks/components/ClientSelect';
import useFile from '@/modules/tasks/views/TasksDashboard/hooks/useFile';
import {
  CreateFileInput,
  CreateMediaInput,
  IconSettingsInput,
  IconTypeEnum,
  UpdateFileInput,
  VisibilitySettingsAccessTypeEnum,
  VisibilitySettingsType,
} from '@/types/schema';

import NameAndEmojiInput from '../NameAndEmojiInput';
import StagesInput from '../StagesInput';

export const INITIAL_VALUES: ProjectEditorFormValues = {
  name: '',
  description: '',
  iconSettings: undefined,
  media: undefined,
  dueDate: undefined,
  projectOwnerId: undefined,
  clientIds: [],
  visibilitySettings: {
    accessType: VisibilitySettingsAccessTypeEnum.Open,
  } as VisibilitySettingsType,
  stages: [],
};

const COVER_IMAGE_STYLES = css`
  width: 12.4rem;
  height: 12.4rem;
  border-radius: 9999rem;
`;
const COVER_IMAGE_SIZE = { width: 124, height: 124 };

export const getInitialValues = (
  file?: ReturnType<typeof useFile>['file'],
  initialValues?: Partial<ProjectEditorFormValues>,
): ProjectEditorFormValues => {
  if (!file) {
    // May need to do a better merge of initial values and defaults in the future
    return {
      ...INITIAL_VALUES,
      ...initialValues,
    };
  }

  return {
    name: file.name,
    description: file.description ?? '',
    iconSettings: file.iconSettings ?? undefined,
    media: file.media ?? undefined,
    dueDate: file.dueDate ?? undefined,
    projectOwnerId: file.projectOwnerId ?? undefined,
    clientIds: file.clients?.map((client) => client.id) || [],
    visibilitySettings: file.visibilitySettings as VisibilitySettingsType,
    stages:
      file.stages
        ?.filter((stage) => !!stage)
        ?.map((stage) => ({
          stageId: stage!.stageId ?? undefined,
          name: stage!.name,
          description: stage!.description ?? undefined,
          iconSettings: stage!.iconSettings ?? getRandomIconSettings(),
          progress: stage!.progress,
        })) || [],
  };
};

export type ProjectEditorStageFormValue = {
  stageId?: string | null;
  name: string;
  description?: string | null;
  iconSettings: IconSettingsInput;
  progress?: number;
};

export type ProjectEditorFormValues = {
  name: string;
  description: string;
  iconSettings: IconSettingsInput | undefined;
  media: CreateMediaInput | undefined;
  dueDate: string | undefined;
  projectOwnerId: string | undefined;
  clientIds: string[];
  visibilitySettings: VisibilitySettingsType;
  stages?: ProjectEditorStageFormValue[];
};

const VISIBILITY_SETTINGS_CONTAINER_OVERRIDES: VisibilitySettingsFormFieldProps['overrides'] = {
  restrictedContent: {
    overrides: {
      label: { variant: 'body-short-02' },
      addButton: { className: 'mt-6' },
    },
  },
};

export const getFormFields = (props: {
  t: TFunction;
  projectId?: string;
  defaultCover: CreateMediaInput;
}): Fields<ProjectEditorFormValues> => {
  const { t, projectId, defaultCover } = props;
  return {
    name: {
      name: 'name',
      label: t('Common:Name'),
      type: FieldType.Custom,
      Component: ({ value: nameValue, setFieldValue, error, touched, onChange: onChangeName }) => {
        const iconFieldKey = 'iconSettings';
        const iconValue = (useFormFieldValue(iconFieldKey) as IconSettingsInput)?.value;

        const onChangeIcon = (nextValue: string) => {
          if (nextValue) {
            setFieldValue(
              iconFieldKey,
              {
                type: IconTypeEnum.Emoji,
                value: nextValue,
              },
              true,
            );
          } else {
            setFieldValue(iconFieldKey, undefined, true);
          }
        };

        return (
          <NameAndEmojiInput
            error={touched && error}
            icon={iconValue}
            name={nameValue}
            placeholder="Give a name to the Project"
            onChangeIcon={onChangeIcon}
            onChangeName={onChangeName}
          />
        );
      },
    },
    description: {
      name: 'description',
      label: t('Common:Form.Description'),
      placeholder: t('Common:Form.DescriptionPlaceholder'),
      size: 'large',
      type: FieldType.Text,
      props: {
        characterLimit: 70,
      },
    },
    media: {
      name: 'media',
      type: FieldType.SlowCustom,
      Component: ({ value, onChange }) => {
        return (
          <AddCover
            cover={value}
            coverImageStyles={COVER_IMAGE_STYLES}
            defaultCover={defaultCover}
            description={t('Projects:Editor.MediaImageDescription')}
            disabled={false}
            imageSize={COVER_IMAGE_SIZE}
            overrideError={true}
            title={t('Projects:Editor.MediaImage')}
            onChange={onChange}
          />
        );
      },
    },
    dueDate: {
      name: 'dueDate',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return (
          <Stack direction="col" gapY={8}>
            <Typography className="pl-8" variant="body-short-02">
              {t('Projects:DueDate')}
            </Typography>
            <DatePicker
              pickerProps={{
                variant: DateVariant.Date,
                selectedTime: value ? new Date(value) : undefined,
                onChange: (newDate) => onChange(newDate?.toISOString()),
              }}
              showActionButtons={false}
            />
          </Stack>
        );
      },
    },
    projectOwnerId: {
      name: 'projectOwnerId',
      label: t('Projects:ProjectOwner'),
      type: FieldType.Custom,
      Component: ({ value, onChange, error }) => {
        const { user, fetching } = useUser(value || undefined);
        if (fetching) {
          return null;
        }
        return (
          <UserSearchInput
            clearable
            defaultUser={user}
            initalValue={user?.name}
            placeholder={t('Projects:Editor.ProjectOwnerPlaceholder')}
            onChange={onChange}
          />
        );
      },
    },
    clientIds: {
      name: 'clientIds',
      label: t('Common:Filter.Clients'),
      type: FieldType.Custom,
      Component: ({ value, onChange, error }: any) => {
        return (
          <ClientSelect hideHeader error={error} selectedClientIds={value} onChange={onChange} />
        );
      },
    },
    visibilitySettings: {
      name: 'visibilitySettings',
      label: t('VisibilitySetting:VisibleTo'),
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <VisibilitySettingsFormField
          overrides={VISIBILITY_SETTINGS_CONTAINER_OVERRIDES}
          type="users_and_groups"
          value={value}
          onChange={onChange}
        />
      ),
    },
    stages: {
      name: 'stages',
      label: t('Projects:Stages'),
      type: FieldType.Custom,
      errorPosition: ErrorPosition.Custom,
      Component: ({ value, onChange, error, touched }) => {
        const showError = Boolean(touched && error);
        return (
          <StagesInput
            currentProjectId={projectId}
            error={showError ? error : undefined}
            value={value ?? []}
            onChange={onChange}
          />
        );
      },
    },
  };
};

const SECTION_CLASSES: MultiStepSection['classes'] = { panel: 'w-full px-24 py-32' };

export const getSectionLayout = ({
  t,
  isClientsTaskEnabled,
  isProjectManagementPreReleaseEnabled,
}: {
  t: TFunction;
  isClientsTaskEnabled: boolean;
  isProjectManagementPreReleaseEnabled: boolean;
}): MultiStepSection[] =>
  getEnabledItems<MultiStepSection>([
    {
      id: 'basicInfo',
      name: t('Projects:Editor.BasicInfo'),
      classes: SECTION_CLASSES,
      enabled: true,
      sectionLayout: [
        {
          id: 'basicInfo',
          layout: isClientsTaskEnabled
            ? [
                'name',
                'description',
                'dueDate',
                'projectOwnerId',
                'clientIds',
                'visibilitySettings',
              ]
            : ['name', 'description', 'visibilitySettings'],
        },
        {
          id: 'media',
          layout: ['media'],
        },
      ],
    },
    {
      id: 'stages',
      name: t('Projects:Editor.Stages'),
      classes: SECTION_CLASSES,
      enabled: isProjectManagementPreReleaseEnabled,
      sectionLayout: [
        {
          id: 'stages',
          layout: ['stages'],
        },
      ],
    },
  ]);

export type ProjectEditorStagesErrorType = {
  errorMessage?: string;
  invalidStagesIndexes?: number[];
};

export const validateForm = (t: TFunction) => (values: ProjectEditorFormValues) => {
  const errors: any = {};

  if (isEmpty(values.name)) {
    errors.name = t('Task:NameCannotBeEmpty');
  }

  if (values.stages && !isEmpty(values.stages)) {
    for (let i = 0; i < values.stages.length; i++) {
      const stage = values.stages[i];
      if (isEmpty(stage.name.trim())) {
        errors.stages ??= {
          errorMessage: t('Projects:Editor.StageNameError'),
          invalidStagesIndexes: [],
        };
        errors.stages.invalidStagesIndexes.push(i);
      }
    }
  }

  return errors;
};

export const adaptFormValuesToCreateProjectInput = (
  values: ProjectEditorFormValues,
): Omit<CreateFileInput, 'category' | 'purpose'> => {
  return {
    name: values.name,
    description: values.description,
    iconSettings: values.iconSettings,
    media: values.media,
    dueDate: values.dueDate,
    projectOwnerId: values.projectOwnerId,
    clientIds: values.clientIds,
    visibilitySettings: values.visibilitySettings,
    stages: values.stages?.map((stage) => ({
      name: stage.name.trim(),
      description: stage.description?.trim() ?? undefined,
      iconSettings: stage.iconSettings,
      progress: stage.progress ?? 0,
    })),
  };
};

export const adaptFormValuesToUpdateProjectInput = (
  values: ProjectEditorFormValues,
): Omit<UpdateFileInput, 'fileId' | 'category' | 'purpose'> => {
  return {
    name: values.name,
    description: values.description,
    iconSettings: values.iconSettings?.value ? values.iconSettings : undefined,
    media: values.media,
    dueDate: values.dueDate,
    projectOwnerId: values.projectOwnerId,
    clientIds: values.clientIds,
    visibilitySettings: values.visibilitySettings,
    stages: values.stages
      ? {
          set: values.stages.map((stage) => ({
            stageId: stage.stageId ?? undefined,
            name: stage.name.trim(),
            description: stage.description?.trim() ?? undefined,
            iconSettings: stage.iconSettings,
            progress: stage.progress ?? 0,
          })),
        }
      : undefined,
  };
};
